import clsx from "clsx";
import { forwardRef, memo, useMemo } from "react";
import { RegisterOptions, useController } from "react-hook-form";
import mergeRefs from "react-merge-refs";

import ErrorMessage from "./ErrorMessage";
import RawTextField, { RawTextFieldProps } from "./RawTextField";

export interface TextFieldProps extends RawTextFieldProps {
  name: string;
  rules?: RegisterOptions;
  showError?: boolean;
  defaultValue?: string;
  shouldUnregister?: boolean;
  errorMessage?: string;
  addComma?: boolean;
}

const TextField = memo(
  forwardRef<HTMLInputElement, TextFieldProps>(
    (
      {
        name,
        className,
        required,
        rules,
        showError = true,
        defaultValue = "",
        shouldUnregister,
        pattern,
        errorMessage,
        error: errorParent,
        addComma,
        ...rest
      },
      ref,
    ) => {
      const {
        field,
        fieldState: { error },
      } = useController({
        name,
        rules: {
          required: required
            ? typeof required === "string"
              ? required
              : "이 항목은 필수입니다."
            : false,
          ...rules,
        },
        defaultValue,
        shouldUnregister,
      });

      const value = useMemo(() => {
        if (!field.value) {
          return field.value;
        }
        if (addComma) {
          return `${field.value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return field.value;
      }, [field.value]);

      return (
        <div className={clsx("relative", className)}>
          <RawTextField
            {...rest}
            value={value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={mergeRefs([field.ref, ref])}
            error={!!error || errorParent}
            required={!!required}
            pattern={pattern}
          />
          {errorMessage ? (
            <ErrorMessage absolute>{errorMessage}</ErrorMessage>
          ) : (
            showError &&
            error?.message &&
            typeof error.message === "string" && (
              <ErrorMessage>{error.message}</ErrorMessage>
            )
          )}
        </div>
      );
    },
  ),
);

TextField.displayName = "TextField";

export default TextField;
